import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';

const VideoGallery = ({ onBackToDashboard, onViewSettings, onViewSubscription }) => {
  const { user, logout } = useAuth();
  const [showMenu, setShowMenu] = useState(false);
  const isLoggedIn = !!user;

  // Sample video data
  const videos = [
    {
      id: 1,
      title: 'Introduction to Platform',
      thumbnail: 'https://via.placeholder.com/400x225/1a1a1a/ffffff?text=Video+1',
      duration: '12:45',
      isLocked: !isLoggedIn
    },
    {
      id: 2,
      title: 'Advanced Techniques',
      thumbnail: 'https://via.placeholder.com/400x225/1a1a1a/ffffff?text=Video+2',
      duration: '18:30',
      isLocked: !isLoggedIn
    },
    {
      id: 3,
      title: 'Pro Tips and Tricks',
      thumbnail: 'https://via.placeholder.com/400x225/1a1a1a/ffffff?text=Video+3',
      duration: '25:15',
      isLocked: !isLoggedIn
    },
    {
      id: 4,
      title: 'Behind the Scenes',
      thumbnail: 'https://via.placeholder.com/400x225/1a1a1a/ffffff?text=Video+4',
      duration: '15:00',
      isLocked: !isLoggedIn
    },
    {
      id: 5,
      title: 'Q&A Session',
      thumbnail: 'https://via.placeholder.com/400x225/1a1a1a/ffffff?text=Video+5',
      duration: '32:20',
      isLocked: !isLoggedIn
    },
    {
      id: 6,
      title: 'Exclusive Content',
      thumbnail: 'https://via.placeholder.com/400x225/1a1a1a/ffffff?text=Video+6',
      duration: '45:10',
      isLocked: !isLoggedIn
    },
    {
      id: 7,
      title: 'Masterclass Part 1',
      thumbnail: 'https://via.placeholder.com/400x225/1a1a1a/ffffff?text=Video+7',
      duration: '28:45',
      isLocked: !isLoggedIn
    },
    {
      id: 8,
      title: 'Masterclass Part 2',
      thumbnail: 'https://via.placeholder.com/400x225/1a1a1a/ffffff?text=Video+8',
      duration: '31:55',
      isLocked: !isLoggedIn
    }
  ];

  const handleVideoClick = (video) => {
    if (video.isLocked) {
      alert('Please log in to access this content');
    } else {
      console.log('Playing video:', video.title);
      // Handle video playback
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-black to-gray-900 text-white">
      {/* Navigation Bar */}
      <nav className="bg-gray-900/80 backdrop-blur-lg border-b border-gray-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo & Back Button */}
            <div className="flex items-center space-x-4">
              {onBackToDashboard && (
                <button
                  onClick={onBackToDashboard}
                  className="flex items-center text-gray-400 hover:text-white transition"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  <span className="ml-2 hidden sm:inline">Back</span>
                </button>
              )}
              <div className="flex items-center">
                <div className="bg-gradient-to-r from-red-600 to-purple-600 p-2 rounded-lg">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <span className="ml-3 text-xl font-bold text-white">Video Library</span>
              </div>
            </div>

            {/* User Menu */}
            {user && (
              <div className="relative">
                <button
                  onClick={() => setShowMenu(!showMenu)}
                  className="flex items-center space-x-3 px-4 py-2 rounded-lg hover:bg-gray-800 transition"
                >
                  <div className="w-10 h-10 bg-gradient-to-r from-red-600 to-purple-600 rounded-full flex items-center justify-center">
                    <span className="text-white font-semibold">
                      {user?.name?.charAt(0).toUpperCase() || 'U'}
                    </span>
                  </div>
                  <span className="text-white font-medium hidden sm:block">{user?.name}</span>
                  <svg className={`w-4 h-4 text-gray-400 transition-transform ${showMenu ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {showMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-gray-800 rounded-lg shadow-xl border border-gray-700 py-2">
                    <div className="px-4 py-2 border-b border-gray-700">
                      <p className="text-sm text-gray-400">Signed in as</p>
                      <p className="text-sm font-medium text-white truncate">{user?.email}</p>
                    </div>
                    {onViewSettings && (
                      <button
                        onClick={onViewSettings}
                        className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 transition"
                      >
                        Account Settings
                      </button>
                    )}
                    {onViewSubscription && (
                      <button
                        onClick={onViewSubscription}
                        className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 transition"
                      >
                        Subscription
                      </button>
                    )}
                    <button
                      onClick={logout}
                      className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-gray-700 transition"
                    >
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Header */}
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8 text-center">
          <h1 className="text-4xl font-bold mb-2">{isLoggedIn ? 'All Videos' : 'Exclusive Video Library'}</h1>
          <p className="text-gray-400">
            {isLoggedIn 
              ? 'Welcome back! Enjoy unlimited access to all content.' 
              : 'Sign in to unlock premium content'}
          </p>
        </div>

        {/* Video Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {videos.map((video) => (
            <div
              key={video.id}
              onClick={() => handleVideoClick(video)}
              className="group relative cursor-pointer transform transition-all duration-300 hover:scale-105"
            >
              {/* Video Card */}
              <div className="relative bg-gray-800 rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-300">
                {/* Thumbnail Container */}
                <div className="relative aspect-video overflow-hidden">
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                  />
                  
                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-300" />
                  
                  {/* Duration Badge */}
                  <div className="absolute bottom-2 right-2 bg-black/80 px-2 py-1 rounded text-xs font-semibold">
                    {video.duration}
                  </div>

                  {/* Lock Icon Overlay */}
                  {video.isLocked && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm">
                      <div className="text-center">
                        <svg
                          className="w-16 h-16 mx-auto mb-2 text-red-500 drop-shadow-lg"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fillRule="evenodd"
                            d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span className="text-sm font-semibold text-red-400">LOCKED</span>
                      </div>
                    </div>
                  )}

                  {/* Play Button Overlay (when not locked) */}
                  {!video.isLocked && (
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="bg-red-600 rounded-full p-4 transform scale-90 group-hover:scale-100 transition-transform duration-300">
                        <svg
                          className="w-8 h-8 text-white"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    </div>
                  )}
                </div>

                {/* Video Info */}
                <div className="p-4">
                  <h3 className="text-lg font-semibold line-clamp-2 group-hover:text-red-500 transition-colors duration-300">
                    {video.title}
                  </h3>
                  {video.isLocked && (
                    <p className="text-sm text-gray-400 mt-2 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                      </svg>
                      Premium Content
                    </p>
                  )}
                </div>
              </div>

              {/* Hover Glow Effect */}
              <div className="absolute -inset-0.5 bg-gradient-to-r from-red-600 to-purple-600 rounded-lg opacity-0 group-hover:opacity-20 blur transition duration-300 -z-10" />
            </div>
          ))}
        </div>

        {/* Call to Action for non-logged in users */}
        {!isLoggedIn && (
          <div className="mt-12 text-center">
            <div className="bg-gradient-to-r from-red-600 to-purple-600 rounded-lg p-8 max-w-2xl mx-auto">
              <h2 className="text-2xl font-bold mb-4">Unlock All Premium Content</h2>
              <p className="text-gray-200 mb-6">
                Get unlimited access to exclusive videos, masterclasses, and behind-the-scenes content.
              </p>
              <button className="bg-white text-red-600 font-bold px-8 py-3 rounded-full hover:bg-gray-100 transition-colors duration-300 transform hover:scale-105">
                Sign In Now
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoGallery;
